/**
 * Compact, self-contained sim engine — same event-pool pattern as the PM Game
 * engine (pure step function, effects applied for real, eligibility instead
 * of a fixed scene chain, delayed consequences via `schedules`), kept as its
 * own small file rather than sharing `src/engine/` so this game's simpler
 * metric set doesn't couple to that one.
 */

export interface Effect {
  metric: string;
  delta: number;
}

export interface FutureEvent {
  eventId: string;
  eligibleOnDay: number;
}

export interface Choice {
  id: string;
  text: string;
  effects: Effect[];
  resultText: string;
  schedules?: FutureEvent[];
}

export type EventKind = "decision" | "risk" | "gate";

export interface GameEvent {
  id: string;
  title: string;
  sender: string;
  day: string;
  kind: EventKind;
  body: string[];
  eligibleWhen: (state: GameState) => boolean;
  choices: Choice[];
}

export interface DecisionRecord {
  day: number;
  eventTitle: string;
  choiceText: string;
  resultText: string;
  effects: Effect[];
}

export interface GameState {
  day: number;
  metrics: Record<string, number>;
  activeEventIds: string[];
  resolvedEventIds: string[];
  pendingEvents: FutureEvent[];
  history: DecisionRecord[];
}

export interface Outcome {
  id: string;
  label: string;
  when: (state: GameState) => boolean;
  summary: string;
}

export interface Game {
  title: string;
  premise: string[];
  initialMetrics: Record<string, number>;
  events: GameEvent[];
  outcomes: Outcome[];
  endWhen: (state: GameState) => boolean;
}

function clamp(n: number): number {
  return Math.max(0, Math.min(100, n));
}

function resolveEligible(state: GameState, game: Game): string[] {
  const known = new Set([...state.activeEventIds, ...state.resolvedEventIds]);
  const due = state.pendingEvents
    .filter((p) => p.eligibleOnDay <= state.day)
    .map((p) => p.eventId)
    .filter((id) => !known.has(id));
  const conditional = game.events
    .filter((e) => !known.has(e.id) && !due.includes(e.id) && e.eligibleWhen(state))
    .map((e) => e.id);
  return Array.from(new Set([...due, ...conditional]));
}

export function startGame(game: Game): GameState {
  const base: GameState = {
    day: 1,
    metrics: { ...game.initialMetrics },
    activeEventIds: [],
    resolvedEventIds: [],
    pendingEvents: [],
    history: [],
  };
  return { ...base, activeEventIds: resolveEligible(base, game) };
}

export function choose(game: Game, state: GameState, eventId: string, choiceId: string): GameState {
  const event = game.events.find((e) => e.id === eventId);
  const choice = event?.choices.find((c) => c.id === choiceId);
  if (!event || !choice) return state;

  const metrics = { ...state.metrics };
  for (const effect of choice.effects) {
    metrics[effect.metric] = clamp((metrics[effect.metric] ?? 0) + effect.delta);
  }

  let next: GameState = {
    ...state,
    metrics,
    activeEventIds: state.activeEventIds.filter((id) => id !== eventId),
    resolvedEventIds: [...state.resolvedEventIds, event.id],
    pendingEvents: [...state.pendingEvents, ...(choice.schedules ?? [])],
    history: [
      ...state.history,
      {
        day: state.day,
        eventTitle: event.title,
        choiceText: choice.text,
        resultText: choice.resultText,
        effects: choice.effects,
      },
    ],
  };

  while (!game.endWhen(next) && next.activeEventIds.length === 0) {
    next = { ...next, day: next.day + 1 };
    next = { ...next, activeEventIds: resolveEligible(next, game) };
  }

  return next;
}

export function isOver(game: Game, state: GameState): boolean {
  return game.endWhen(state);
}

export function resolveOutcome(game: Game, state: GameState): Outcome {
  return game.outcomes.find((o) => o.when(state)) ?? game.outcomes[game.outcomes.length - 1];
}
